import type { Order } from "@daytrip/legacy-models";
import { isEqual, omit } from "lodash";

export type OrderChangeType =
    | "passenger_count"
    | "passenger_name"
    | "passenger_email"
    | "passenger_phone"
    | "passenger_child_seat"
    | "passenger_details"
    | "passenger_luggage"
    | "vehicle_type"
    | "departure_date"
    | "origin_location"
    | "destination_location"
    | "pickup_address"
    | "dropoff_address"
    | "price"
    | "customer_note"
    | "driver_note"
    | "flight_number";

export interface OrderChanges {
    // High-level flag
    hasChanges: boolean;

    // Changes as array of change types (scalable pattern - follows Amplitude best practices)
    changeTypes: OrderChangeType[];
    changeCount: number;

    // Price change metadata
    priceChangeAmount?: number; // EUR amount of price change (can be negative for discounts)
}

/**
 * Detects changes between original and updated Order.
 *
 * This pure function compares two Order objects and returns detailed information
 * about what changed, enabling tracking of CS manual interventions in Amplitude.
 *
 * Only tracks changes for accepted orders (orders with acceptedAt set).
 *
 * @param originalOrder - The order before edits
 * @param updatedOrder - The order after edits
 * @returns Detailed breakdown of all changes, or empty changes if order not accepted
 *
 * @example
 * const changes = detectOrderChanges(originalOrder, updatedOrder);
 * if (changes.hasChanges) {
 *   console.log('Changed fields:', changes.changeTypes);
 * }
 */
export function detectOrderChanges(originalOrder: Order, updatedOrder: Order): OrderChanges {
    const changes: OrderChanges = {
        hasChanges: false,
        changeTypes: [],
        changeCount: 0,
    };

    // Only track changes for accepted orders
    if (!originalOrder.acceptedAt) {
        return changes;
    }

    // Passenger count
    if (originalOrder.passengers.length !== updatedOrder.passengers.length) {
        changes.changeTypes.push("passenger_count");
    }

    // Get overlapping passenger count for granular field comparisons
    const minPassengerCount = Math.min(originalOrder.passengers.length, updatedOrder.passengers.length);

    // Passenger name changes (firstName or lastName) - check overlapping passengers only
    if (minPassengerCount > 0) {
        const originalNames = originalOrder.passengers
            .slice(0, minPassengerCount)
            .map((p) => ({ firstName: p.firstName, lastName: p.lastName }));
        const updatedNames = updatedOrder.passengers
            .slice(0, minPassengerCount)
            .map((p) => ({ firstName: p.firstName, lastName: p.lastName }));

        if (!isEqual(originalNames, updatedNames)) {
            changes.changeTypes.push("passenger_name");
        }
    }

    // Passenger email changes - check overlapping passengers only
    if (minPassengerCount > 0) {
        const originalEmails = originalOrder.passengers.slice(0, minPassengerCount).map((p) => p.email);
        const updatedEmails = updatedOrder.passengers.slice(0, minPassengerCount).map((p) => p.email);

        if (!isEqual(originalEmails, updatedEmails)) {
            changes.changeTypes.push("passenger_email");
        }
    }

    // Passenger phone changes - check overlapping passengers only
    if (minPassengerCount > 0) {
        const originalPhones = originalOrder.passengers.slice(0, minPassengerCount).map((p) => p.phoneNumber);
        const updatedPhones = updatedOrder.passengers.slice(0, minPassengerCount).map((p) => p.phoneNumber);

        if (!isEqual(originalPhones, updatedPhones)) {
            changes.changeTypes.push("passenger_phone");
        }
    }

    // Passenger child seat changes - check overlapping passengers only
    if (minPassengerCount > 0) {
        const originalChildSeats = originalOrder.passengers.slice(0, minPassengerCount).map((p) => p.childSeatType);
        const updatedChildSeats = updatedOrder.passengers.slice(0, minPassengerCount).map((p) => p.childSeatType);

        if (!isEqual(originalChildSeats, updatedChildSeats)) {
            changes.changeTypes.push("passenger_child_seat");
        }
    }

    // Passenger details fallback (edge case fields: type, birthdayAt, countryId,
    // userId, assignationId, sharedPosition - excluding specifically tracked fields)
    // Compare overlapping passengers only, omitting all specifically tracked fields
    if (minPassengerCount > 0) {
        const fieldsToOmit = ["luggage", "firstName", "lastName", "email", "phoneNumber", "childSeatType"];
        const originalPassengersEdgeCaseFields = originalOrder.passengers
            .slice(0, minPassengerCount)
            .map((p) => omit(JSON.parse(JSON.stringify(p)), fieldsToOmit));
        const updatedPassengersEdgeCaseFields = updatedOrder.passengers
            .slice(0, minPassengerCount)
            .map((p) => omit(JSON.parse(JSON.stringify(p)), fieldsToOmit));

        if (!isEqual(originalPassengersEdgeCaseFields, updatedPassengersEdgeCaseFields)) {
            changes.changeTypes.push("passenger_details");
        }
    }

    // Passenger luggage (tracked separately from other passenger details)
    // Compare luggage for passengers that exist in both orders (overlapping passengers)
    if (minPassengerCount > 0) {
        const originalPassengersLuggage = originalOrder.passengers.slice(0, minPassengerCount).map((p) => p.luggage);
        const updatedPassengersLuggage = updatedOrder.passengers.slice(0, minPassengerCount).map((p) => p.luggage);

        if (!isEqual(originalPassengersLuggage, updatedPassengersLuggage)) {
            changes.changeTypes.push("passenger_luggage");
        }
    }

    // Vehicle types
    if (!isEqual(originalOrder.vehicles, updatedOrder.vehicles)) {
        changes.changeTypes.push("vehicle_type");
    }

    // Departure date
    const originalOrderDepartureAt = new Date(originalOrder.departureAt);
    if (originalOrderDepartureAt.getTime() !== updatedOrder.departureAt.getTime()) {
        changes.changeTypes.push("departure_date");
    }

    // Locations (track each location type separately for granular analytics)
    if (originalOrder.originLocationId !== updatedOrder.originLocationId) {
        changes.changeTypes.push("origin_location");
    }

    if (originalOrder.destinationLocationId !== updatedOrder.destinationLocationId) {
        changes.changeTypes.push("destination_location");
    }

    if (originalOrder.pickupAddress !== updatedOrder.pickupAddress) {
        changes.changeTypes.push("pickup_address");
    }

    if (originalOrder.dropoffAddress !== updatedOrder.dropoffAddress) {
        changes.changeTypes.push("dropoff_address");
    }

    // Price
    if (originalOrder.totalPrice !== updatedOrder.totalPrice) {
        changes.changeTypes.push("price");
        // Calculate price change amount (negative = discount, positive = increase)
        if (originalOrder.totalPrice != null && updatedOrder.totalPrice != null) {
            changes.priceChangeAmount = updatedOrder.totalPrice - originalOrder.totalPrice;
        }
    }

    // Notes (track each note type separately for more granular insights)
    if (originalOrder.customerNote !== updatedOrder.customerNote) {
        changes.changeTypes.push("customer_note");
    }

    if (originalOrder.driverNote !== updatedOrder.driverNote) {
        changes.changeTypes.push("driver_note");
    }

    // Flight number
    if (originalOrder.flightNumber !== updatedOrder.flightNumber) {
        changes.changeTypes.push("flight_number");
    }

    changes.hasChanges = changes.changeTypes.length > 0;
    changes.changeCount = changes.changeTypes.length;

    return changes;
}
